import { fmtDT } from "@/lib/api";

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
      <div>
        <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900">{title}</h1>
        {subtitle && <p className="text-base font-medium text-slate-500 mt-1">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

export function MetricCard({ label, value, icon: Icon, tone = "indigo", testId }) {
  const tones = {
    indigo: "bg-indigo-50 text-indigo-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    sky: "bg-sky-50 text-sky-600",
    rose: "bg-rose-50 text-rose-600",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-sm transition-shadow duration-200" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-500">{label}</span>
        {Icon && <span className={`h-9 w-9 rounded-lg grid place-items-center ${tones[tone]}`}><Icon className="h-5 w-5" /></span>}
      </div>
      <div className="mt-3 text-2xl font-extrabold text-slate-900 num tracking-tight">{value}</div>
    </div>
  );
}

export function ProgressBar({ current, target, showLabels = true }) {
  const pct = target ? Math.min((current / target) * 100, 100) : 0;
  return (
    <div>
      {showLabels && (
        <div className="flex items-center justify-between mb-1.5 text-sm">
          <span className="font-semibold text-emerald-700 num">{fmtDT(current)}</span>
          <span className="font-medium text-slate-400 num">Objectif {fmtDT(target)}</span>
        </div>
      )}
      <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full bg-emerald-500 transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      {showLabels && <div className="mt-1 text-xs font-medium text-slate-400 num">{pct.toFixed(0)}% atteint</div>}
    </div>
  );
}

const STATUS = {
  open: { label: "Ouverte", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  closed: { label: "Clôturée", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  transmitted: { label: "Transmise", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  draft: { label: "Brouillon", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  active: { label: "Actif", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  pending: { label: "En attente", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  rejected: { label: "Refusé", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  validated: { label: "Validée", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export function StatusBadge({ status }) {
  const s = STATUS[status] || { label: status, cls: "bg-slate-100 text-slate-600 border-slate-200" };
  return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${s.cls}`}>{s.label}</span>;
}

export function TypeBadge({ type }) {
  const map = {
    circulaire: "bg-indigo-50 text-indigo-700",
    communique: "bg-sky-50 text-sky-700",
    note: "bg-slate-100 text-slate-700",
    alerte: "bg-rose-50 text-rose-700",
    reglementation: "bg-amber-50 text-amber-700",
    info: "bg-emerald-50 text-emerald-700",
    avis: "bg-violet-50 text-violet-700",
  };
  return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${map[type] || "bg-slate-100 text-slate-700"}`}>{type}</span>;
}

export function Loading() {
  return (
    <div className="py-20 grid place-items-center">
      <div className="h-8 w-8 rounded-full border-2 border-indigo-600 border-t-transparent animate-spin" />
    </div>
  );
}

export function EmptyState({ title, subtitle, icon: Icon }) {
  return (
    <div className="py-16 text-center border border-dashed border-slate-200 rounded-xl bg-white">
      {Icon && <Icon className="h-10 w-10 text-slate-300 mx-auto mb-3" />}
      <div className="font-bold text-slate-700">{title}</div>
      {subtitle && <div className="text-sm text-slate-400 mt-1">{subtitle}</div>}
    </div>
  );
}
